import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import { fetchExistingApplication, setApiToken, setApiTokenForVersion } from '../api';
import {
  setPartnerIntroViewed,
  setPartnerFromApplication,
  setAvailablePolicyTypes,
  setApplicationFromServer,
  fatalError,
  setExistingApplicationLoaded,
  showNextPage,
  didViewAdditionalInsureds,
  didViewGLPage,
  clearApplication,
  didViewAddOnsPage,
  setUserHasExistingApplication,
} from './application';
import { getRequestContext, reportToSentry } from '../helpers/sentry';
import { applicationError } from './quote';
import handleGetIndustryName from './handleGetIndustryName';
import { glValuesComplete } from '../selectors/routing';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { api_versions, POLICY_DEFAULTS } from '../constants';
import { setAgent } from './agent';
import { setReferralPartner } from './referralPartner';
import { setLossControlValueHelper } from '../helpers/lossControl';

const loadExistingApplication = (
  dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>,
  getState: () => ReduxState,
  incomingApplicationId: string
) => {
  LogRocket.track('application_bridge', { applicationId: incomingApplicationId });
  return fetchExistingApplication(incomingApplicationId)
    .then((res) => {
      const { agent, application, availablePolicyTypes, referralPartner, partner } = res.data;
      const hasBop = application?.applicationTypes?.includes('BOP');
      const hasGL = application?.applicationTypes?.includes('GL');
      const lossControlOverride =
        hasBop || hasGL
          ? { lossControl: setLossControlValueHelper(application.lossControl, POLICY_DEFAULTS.GL_BOP.lossControl) }
          : {};

      // when we receive the limitDamagePremRented value from the api, we want to increase it by 50k
      // to show the total with the base value of 50k
      const limitDamagePremRentedOverride = application?.limitDamagePremRented
        ? { limitDamagePremRented: application?.limitDamagePremRented + 50_000 }
        : {};

      const applicationWithDefaults = {
        ...application,
        ...lossControlOverride,
        ...limitDamagePremRentedOverride,
      };

      dispatch(clearApplication());
      dispatch(setPartnerIntroViewed(false));
      dispatch(didViewAdditionalInsureds());
      dispatch(didViewAddOnsPage());
      dispatch(setUserHasExistingApplication(true));

      if (!res.data || !application) {
        reportToSentry(`Couldn't load existing application: ${incomingApplicationId}`, {
          name: 'api response',
          data: res,
        });
        dispatch(applicationError(["Couldn't load application"]));
      } else {
        dispatch(setExistingApplicationLoaded(incomingApplicationId));

        Sentry.setUser({ id: incomingApplicationId, email: application.contactEmail });
        LogRocket.identify(incomingApplicationId, {
          email: application.contactEmail ?? '',
          name: [application.contactFirstName, application.contactLastName].filter(Boolean).join(' '),
          agencyName: agent?.agencyName ?? '',
          producerExternalID: agent?.producerExternalID ?? '',
        });

        if (glValuesComplete(application)) {
          dispatch(didViewGLPage());
        }

        if (application?.industryId) {
          dispatch(handleGetIndustryName(application.industryId) as any);
        }

        if (agent) {
          dispatch(setAgent(agent));
        }

        if (partner) {
          dispatch(setPartnerFromApplication(partner));
          if (agent?.producerExternalID) {
            setApiTokenForVersion(api_versions.V1, partner.id);
          } else {
            setApiToken(partner.id);
          }
        }

        if (referralPartner?.id) {
          setApiTokenForVersion(api_versions.V1_6, referralPartner.id);
          dispatch(setReferralPartner(referralPartner));
        }

        dispatch(setAvailablePolicyTypes(availablePolicyTypes));
        dispatch(setApplicationFromServer(applicationWithDefaults));

        if (incomingApplicationId) {
          LogRocket.track('application_id', { applicationId: incomingApplicationId });
        }

        dispatch(showNextPage());
      }
    })
    .catch((error) => {
      reportToSentry(error, getRequestContext({ incomingApplicationId }));
      dispatch(fatalError(error));
      return Promise.reject(error);
    });
};

export default loadExistingApplication;
